import { useMemo, useState } from 'react';

const tabs = [
  { key: 'movies', label: 'Movies / Shows', icon: '🎬' },
  { key: 'music', label: 'Music / Playlists', icon: '🎵' },
  { key: 'games', label: 'Games / Board Games', icon: '🎮' },
];

const scenes = ['All', 'Relax', 'Soothing', 'Lively', 'Upbeat', 'Focused', 'Thrilling', 'Thoughtful', 'Party'];

function EntertainmentSection({ entertainmentData, onAdd, onDelete }) {
  const [activeTab, setActiveTab] = useState('movies');
  const [drafts, setDrafts] = useState({
    movies: { title: '', mood: '', tag: '', scene: '' },
    music: { title: '', mood: '', tag: '', scene: '' },
    games: { title: '', mood: '', tag: '', scene: '' },
  });
  const [sceneFilter, setSceneFilter] = useState('All');
  const [inspiration, setInspiration] = useState(null);

  const filteredList = useMemo(() => {
    const list = entertainmentData[activeTab] || [];
    if (sceneFilter === 'All') return list;
    return list.filter((item) => (item.scene || '').includes(sceneFilter) || (item.tag || '').includes(sceneFilter));
  }, [activeTab, entertainmentData, sceneFilter]);

  const handleChange = (category, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [category]: { ...prev[category], [field]: value },
    }));
  };

  const handleAdd = (category) => {
    const item = drafts[category];
    if (!item.title.trim()) return;
    onAdd?.(category, { ...item });
    setDrafts((prev) => ({
      ...prev,
      [category]: { title: '', mood: '', tag: '', scene: '' },
    }));
  };

  const handleInspire = () => {
    const pick = (key) => {
      const list = entertainmentData[key] || [];
      const filtered =
        sceneFilter === 'All'
          ? list
          : list.filter((i) => (i.scene || '').includes(sceneFilter) || (i.tag || '').includes(sceneFilter));
      const pool = filtered.length ? filtered : list;
      return pool[Math.floor(Math.random() * pool.length)];
    };
    setInspiration({
      movie: pick('movies'),
      music: pick('music'),
      game: pick('games'),
      scene: sceneFilter === 'All' ? 'Freestyle' : sceneFilter,
    });
  };

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Entertainment Hub</p>
          <h2>Curated + Custom</h2>
          <p className="sub small">Filter by vibe or shuffle a combo in one tap</p>
        </div>
        <div className="tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="chips-row scene-row">
        {scenes.map((s) => (
          <button
            key={s}
            className={`chip ${sceneFilter === s ? 'active' : 'soft'}`}
            onClick={() => setSceneFilter(s)}
          >
            {s}
          </button>
        ))}
        <button className="secondary ghost" onClick={handleInspire}>
          🎲 Inspiration Shuffle
        </button>
      </div>

      {inspiration && (
        <div className="inspire-card">
          <div className="pill muted">Scene · {inspiration.scene}</div>
          <div className="inspire-grid">
            {inspiration.movie && (
              <div className="inspire-item">
                <div className="pill">Movie</div>
                <div className="item-title">{inspiration.movie.title}</div>
                <div className="item-sub">{inspiration.movie.mood}</div>
              </div>
            )}
            {inspiration.music && (
              <div className="inspire-item">
                <div className="pill">Music</div>
                <div className="item-title">{inspiration.music.title}</div>
                <div className="item-sub">{inspiration.music.mood}</div>
              </div>
            )}
            {inspiration.game && (
              <div className="inspire-item">
                <div className="pill">Game</div>
                <div className="item-title">{inspiration.game.title}</div>
                <div className="item-sub">{inspiration.game.mood}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card compact">
        <div className="card-head">
          <span className="card-icon">{tabs.find((t) => t.key === activeTab)?.icon}</span>
          <div>
            <p className="eyebrow">{tabs.find((t) => t.key === activeTab)?.label}</p>
            <h3>Current list (delete / add)</h3>
          </div>
        </div>
        <ul className="list">
          {filteredList.map((item) => (
            <li key={item.id} className="list-row">
              <div>
                <div className="item-title">{item.title}</div>
                <div className="item-sub">
                  {item.mood} · {item.tag}
                  {item.scene ? ` · ${item.scene}` : ''}
                </div>
              </div>
              <button className="pill danger" onClick={() => onDelete?.(activeTab, item.id)}>
                Delete
              </button>
            </li>
          ))}
          {!filteredList.length && <li className="item-sub">No entries yet—add one!</li>}
        </ul>

        <div className="form-row">
          <input
            className="input"
            placeholder="Title"
            value={drafts[activeTab].title}
            onChange={(e) => handleChange(activeTab, 'title', e.target.value)}
          />
          <input
            className="input"
            placeholder="Mood / Description"
            value={drafts[activeTab].mood}
            onChange={(e) => handleChange(activeTab, 'mood', e.target.value)}
          />
          <input
            className="input"
            placeholder="Tag"
            value={drafts[activeTab].tag}
            onChange={(e) => handleChange(activeTab, 'tag', e.target.value)}
          />
          <input
            className="input"
            placeholder="Scene (optional, e.g., Relax/Lively)"
            value={drafts[activeTab].scene}
            onChange={(e) => handleChange(activeTab, 'scene', e.target.value)}
          />
          <button className="secondary" onClick={() => handleAdd(activeTab)}>
            Add
          </button>
        </div>
      </div>
    </section>
  );
}

export default EntertainmentSection;

